import React, { useState, useRef, useEffect } from 'react';
import { Info, X } from '@phosphor-icons/react';

/**
 * Small click-popover field-level help. Designed to sit inline next to a
 * <Label>. Mobile-friendly (click, not hover) and accessibility-friendly
 * (works with keyboard, has aria attrs).
 *
 * Usage:
 *   <Label className="flex items-center gap-1">
 *     401(k) <FieldHelp title="401(k)" body="Employer-sponsored retirement..." suggestion="Enter the current balance from your most recent statement." />
 *   </Label>
 */
const FieldHelp = ({ title, body, suggestion, testid = 'field-help' }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <span className="relative inline-flex items-center" ref={wrapperRef}>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); setOpen((s) => !s); }}
        data-testid={testid}
        aria-label={`Show help for ${title}`}
        aria-expanded={open}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[#4A5568] hover:text-[#C5A059] focus:outline-none focus:ring-2 focus:ring-[#C5A059]/50 transition-colors"
      >
        <Info size={14} weight="duotone" />
      </button>
      {open && (
        <div
          data-testid={`${testid}-popover`}
          role="dialog"
          aria-labelledby={`${testid}-title`}
          className="absolute left-6 top-1/2 -translate-y-1/2 z-50 w-72 max-w-[calc(100vw-2rem)] bg-white rounded-md shadow-2xl border border-slate-200 overflow-hidden"
        >
          <div className="bg-[#0A1128] px-3 py-2 flex items-start justify-between gap-2">
            <p id={`${testid}-title`} className="text-xs font-semibold text-[#C5A059] flex-1">{title}</p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid={`${testid}-close`}
              className="text-white/70 hover:text-white"
              aria-label="Close help"
            >
              <X size={14} />
            </button>
          </div>
          <div className="p-3 space-y-2">
            <p className="text-xs text-[#1A202C] leading-relaxed">{body}</p>
            {suggestion && (
              <p className="text-xs text-[#C5A059] leading-relaxed bg-[#C5A059]/5 border-l-2 border-[#C5A059] pl-2 py-1">
                <strong className="font-semibold">Suggested:</strong> {suggestion}
              </p>
            )}
          </div>
        </div>
      )}
    </span>
  );
};

export default FieldHelp;
